import {AfterViewChecked, Directive, ElementRef, Input} from "@angular/core";

@Directive({
    selector: '[display-in-one-line]',
    standalone: false
})
export class OneLineDisplayDirective implements AfterViewChecked {

	/**
	 * If not set, whole text node will be processed
	 */
	@Input('display-in-one-line') oneLineExpr: string;

	/**
	 * If set to true, the given expression will be looked up in all child nodes
	 */
	@Input() recurrent: boolean = true;

	/**
	 * If set to true, the given expression will be treated literally as a string. Otherwise, it will be treated as a regular expression
	 */
	@Input() literal: boolean = false;

	@Input('regex-flags') flags: string;


	constructor(private elementRef: ElementRef) {
	}

	ngAfterViewChecked(): void {
		this.handle(this.elementRef.nativeElement);
	}

	private handle(parent: Node): void {
		let children = this.getNonLiveChildNodesCol(parent);
		children.forEach(child => {
			if (child.nodeType == Node.TEXT_NODE) {
				this.modifyTextNodeIfNeeded(<Text>child, parent);
			} else if (this.recurrent && child.nodeType == Node.ELEMENT_NODE) {
				this.handle(child);
			}
		})
	}

	private getNonLiveChildNodesCol(parent: Node): ChildNode[] {
		let children: ChildNode[] = [];
		parent.childNodes.forEach(child => children.push(child));
		return children;
	}

	private modifyTextNodeIfNeeded(textNode: Text, parent: Node) {
		if (this.shouldModifyTextNode(textNode, parent)) {
			this.split(textNode).forEach(textNodePart => parent.insertBefore(textNodePart, textNode));
			parent.removeChild(textNode);
		}
	}

	private split(textNode: Text): Node[] {
		if (this.oneLineExpr == null || this.oneLineExpr === '') {
			return [this.createOneLineSpanElement(textNode.textContent)];
		}

		if (!this.literal) {
			let regex = new RegExp(this.oneLineExpr, this.getFlags());
			let oneLineTextsArray = this.createArrayWithOneLineTexts(regex, textNode);
			return this.splitTextNode(textNode, regex, oneLineTextsArray);
		}

		return this.splitTextNode(textNode, <string>this.oneLineExpr);
	}

	private createArrayWithOneLineTexts(regexToUse: RegExp, textNode: Text) {
		let result: RegExpExecArray = regexToUse.exec(textNode.textContent);
		let oneLineTextsArray: string[] = [];
		while (result != null) {
			oneLineTextsArray.push(result.input.substring(result.index, regexToUse.lastIndex));
			result = regexToUse.exec(textNode.textContent);
		}
		return oneLineTextsArray;
	}

	private splitTextNode(textNode: Text, separator: string | RegExp, oneLineTextsArray: string[] | null = null): Node[] {
		return textNode.textContent.split(separator).flatMap((part, index, textParts) => {
			let arr: Node[] = part == '' ? [] : [document.createTextNode(part)];
			if (index < textParts.length - 1) {
				let oneLineText: string = oneLineTextsArray == null ? <string>separator : oneLineTextsArray[index];
				arr.push(this.createOneLineSpanElement(oneLineText));
			}

			return arr;
		});
	}

	private shouldModifyTextNode(node: Text, parent: Node): boolean {
		if (parent instanceof HTMLElement && (<HTMLElement>parent).hasAttribute('divided')) {
			return false;
		}

		if (this.oneLineExpr == null || this.oneLineExpr === '') {
			return true;
		}

		return this.literal ?
			node.textContent?.includes(this.oneLineExpr) :
			new RegExp(this.oneLineExpr, this.getFlags()).test(node.textContent);
	}

	private getFlags(): string {
		return this.literal ?
			'' :
			(this.flags != null ? this.flags.replace(/[yg]/, '') + 'g' : 'g');
	}

	private createOneLineSpanElement(withText: string): HTMLElement {
		let oneLineSpan: HTMLSpanElement = document.createElement('span');
		let attr: Attr = document.createAttribute('style');
		attr.value = 'white-space: nowrap;'
		oneLineSpan.setAttributeNode(attr);
		oneLineSpan.setAttributeNode(document.createAttribute('divided'));
		oneLineSpan.appendChild(document.createTextNode(withText));
		return oneLineSpan;
	}
}
